import { gettext } from '@eventbrite/i18n';
import React from 'react';
import { useScrollDispatch } from '../../../contexts/ScrollContext';
import { LocationProps } from '../WhenAndWhere/Location/Location';
import { CalendarIcon, LocationIcon } from './StartDateAndLocationIcons';

import './StartDateAndLocationView.scss';

type StartDateAndLocationViewProps = {
    dateTimeText?: string;
    dateText?: string;
    location?: LocationProps;
    isSeriesParent: boolean;
    isMultipleDates: boolean;
};

type LocationDisplayProps = {
    location: LocationProps;
    onLocationClick: () => void;
};

const OnlineEventLocation = () => (
    <span className="start-date-and-location__location">
        {gettext('Online event')}
    </span>
);

const InPersonLocation = ({
    address,
    onClick,
}: {
    address: string;
    onClick: () => void;
}) => (
    <button
        className="start-date-and-location__location start-date-and-location__location--link"
        onClick={onClick}
        type="button"
    >
        {address}
    </button>
);

const TBDLocation = () => (
    <span className="start-date-and-location__location">
        {gettext('Location TBD')}
    </span>
);

const LocationDisplay = ({
    location,
    onLocationClick,
}: LocationDisplayProps) => {
    if (location.isOnlineEvent) {
        return <OnlineEventLocation />;
    }

    if (location.hasAddress) {
        const venueName = location.venueName;
        const venueMultilineAddress = location.venueMultilineAddress || [];
        const firstAddressLine = venueMultilineAddress[0];

        // Show venue name if available (and not empty), otherwise show first address line
        const displayText = (venueName && venueName.trim()) || firstAddressLine;

        if (displayText) {
            return (
                <InPersonLocation
                    address={displayText}
                    onClick={onLocationClick}
                />
            );
        }
    }

    return <TBDLocation />;
};

export const StartDateAndLocationView = ({
    dateTimeText,
    dateText,
    location,
    isMultipleDates,
}: StartDateAndLocationViewProps) => {
    const scrollTo = useScrollDispatch();

    const handleLocationClick = () => {
        scrollTo({ action: 'scroll', target: 'whenAndWhere' });
    };

    return (
        <div className="start-date-and-location">
            {location && (
                <div className="start-date-and-location__item">
                    <LocationIcon
                        className="start-date-and-location__icon"
                        aria-label="Location"
                    />
                    <LocationDisplay
                        location={location}
                        onLocationClick={handleLocationClick}
                    />
                </div>
            )}
            {isMultipleDates ? (
                <div className="start-date-and-location__item">
                    <CalendarIcon
                        className="start-date-and-location__icon"
                        aria-label="Event dates"
                    />
                    <MultipleDatesView />
                </div>
            ) : (
                <div className="start-date-and-location__item">
                    <CalendarIcon
                        className="start-date-and-location__icon"
                        aria-label="Event date"
                    />
                    <time
                        className="start-date-and-location__date"
                        dateTime={dateTimeText}
                    >
                        {dateText}
                    </time>
                </div>
            )}
        </div>
    );
};

export const MultipleDatesView = () => (
    <span className="start-date">{gettext('Multiple dates')}</span>
);
